import React, { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { paymentAPI } from '../../services/api';
import toast from 'react-hot-toast';

const PackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_days: 30,
    is_active: true,
    is_popular: false,
    display_order: 0,
    features: []
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await paymentAPI.getPackages();
      setPackages(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch packages');
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      // Parse features if it's a string, otherwise use as is
      let features = [];
      if (pkg.features) {
        try {
          features = typeof pkg.features === 'string' ? JSON.parse(pkg.features) : pkg.features;
        } catch (e) {
          console.error('Error parsing features:', e);
          features = [];
        }
      }
      
      setFormData({
        name: pkg.name || '',
        description: pkg.description || '',
        price: pkg.price || '',
        duration_days: pkg.duration_days || 30,
        is_active: pkg.is_active === 1 || pkg.is_active === true,
        is_popular: pkg.is_popular === 1 || pkg.is_popular === true,
        display_order: pkg.display_order || 0,
        features: features
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        duration_days: 30,
        is_active: true,
        is_popular: false,
        display_order: packages.length,
        features: []
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validate form
    if (!formData.name || !formData.price || !formData.duration_days) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        features: formData.features, // Send as array, backend will stringify if needed
        is_active: formData.is_active ? 1 : 0,
        is_popular: formData.is_popular ? 1 : 0
      };

      if (editingPackage) {
        await paymentAPI.updatePackage(editingPackage.id, data);
        toast.success('Package updated successfully');
      } else {
        await paymentAPI.addPackage(data);
        toast.success('Package added successfully');
      }
      setDialogOpen(false);
      fetchPackages();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.message || 'Failed to save package');
    }
  };

  const handleDelete = async (pkg) => {
    if (window.confirm(`Are you sure you want to delete ${pkg.name}?`)) {
      try {
        await paymentAPI.deletePackage(pkg.id);
        toast.success('Package deleted successfully');
        fetchPackages();
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Failed to delete package');
      }
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const renderFeatures = (features) => {
    if (!features) return [];
    try {
      if (typeof features === 'string') {
        return JSON.parse(features);
      }
      return features;
    } catch (e) {
      console.error('Error parsing features:', e);
      return [];
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Package Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)',
            }
          }}
        >
          Add Package
        </Button>
      </Box>

      <Grid container spacing={3}>
        {packages.map((pkg) => (
          <Grid item xs={12} md={6} lg={4} key={pkg.id}>
            <Card sx={{ position: 'relative', height: '100%', borderRadius: 2 }}>
              {pkg.is_popular === 1 || pkg.is_popular === true ? (
                <Chip
                  label="Popular"
                  color="primary"
                  sx={{ position: 'absolute', top: 10, right: 10, backgroundColor: '#e53935' }}
                />
              ) : null}
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#e53935' }}>
                    {pkg.name}
                  </Typography>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenDialog(pkg)} sx={{ color: '#1e88e5' }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(pkg)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="h4" sx={{ color: '#1e88e5', fontWeight: 'bold', mt: 2 }} gutterBottom>
                  ₨ {pkg.price}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  for {pkg.duration_days} days
                </Typography>

                {pkg.description && (
                  <Typography variant="body2" sx={{ mt: 2, mb: 2, color: '#666' }}>
                    {pkg.description}
                  </Typography>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                  Features:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {renderFeatures(pkg.features).map((feature, idx) => (
                    <li key={idx}>
                      <Typography variant="body2" sx={{ color: '#555' }}>{feature}</Typography>
                    </li>
                  ))}
                </ul>

                <Box mt={2}>
                  <Chip
                    label={pkg.is_active === 1 || pkg.is_active === true ? 'Active' : 'Inactive'}
                    color={pkg.is_active === 1 || pkg.is_active === true ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {packages.length === 0 && !loading && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No packages found. Click "Add Package" to create one.
          </Typography>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', color: '#fff' }}>
          {editingPackage ? 'Edit Package' : 'Add New Package'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Package Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Price (NPR)"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Duration (days)"
                type="number"
                value={formData.duration_days}
                onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Features</Typography>
              <Box display="flex" gap={1} mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add a feature (e.g., Profile Boost, Priority Support)"
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                />
                <Button 
                  onClick={addFeature}
                  sx={{ 
                    background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
                    color: '#fff',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)',
                    }
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.features.map((feature, idx) => (
                  <Chip
                    key={idx}
                    label={feature}
                    onDelete={() => removeFeature(idx)}
                    size="small"
                    sx={{ backgroundColor: '#f0f0f0' }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4caf50',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#4caf50',
                      },
                    }}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_popular}
                    onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#e53935',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#e53935',
                      },
                    }}
                  />
                }
                label="Popular"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #c62828 0%, #1565c0 100%)',
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackageManager;